import { useState } from 'react';

import { AuthStatus } from '@app/auth/api/auth.type';
import { AUTH_TOKEN_KEY } from '@app/auth/constant';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { Routes } from '@app/constants/routes';
import { gtag } from '@app/gtag/gtag';
import { EGTAGRegistrationOrigin, EGTAGRegistrationPopUp } from '@app/gtag/gtag.type';
import { useTypedNavigate } from '@app/router';
import { isSafeRedirectPath } from '@app/router/utils/redirectPath.utils';
import { useStore } from '@app/store/useStore.hook';
import localforage from 'localforage';
import { useMount } from 'react-use';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import {
  AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_POPUP,
  AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_SOURCE,
  AUTH_QUERY_PARAM_REDIRECT,
  AUTH_QUERY_PARAM_SIGN_UP_REQUEST_TYPE,
  AUTH_QUERY_PARAM_TOKEN,
} from '../auth.constants';

export const OAuth2AuthorizationPage: React.FC = () => {
  // Read once, directly from the raw URL, on the very first render - not via useQueryParam.
  // Root cause confirmed live on dev (2026-08-18): use-query-params (v1.2.3, query-string
  // serializer) rebuilds the *entire* search string from its own tracked state whenever a
  // setter fires - calling setToken(undefined) here to "clean up" the URL triggered a
  // rewrite that alphabetically re-sorted every param and dropped token, which remounted
  // this page with token === null before the original localforage.setItem/getMe() chain
  // ever got a chance to run. The lazy useState initializer runs exactly once, so this
  // value can no longer be affected by whatever use-query-params (or anything else)
  // subsequently does to the address bar.
  const [token] = useState(() => new URLSearchParams(window.location.search).get(AUTH_QUERY_PARAM_TOKEN));

  const [requestType, setRequestType] = useQueryParam(
    AUTH_QUERY_PARAM_SIGN_UP_REQUEST_TYPE,
    withDefault(StringParam, null)
  );
  const [forwardedSource] = useQueryParam(AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_SOURCE, withDefault(StringParam, null));
  const [forwardedPopup] = useQueryParam(AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_POPUP, withDefault(StringParam, null));
  const [redirectTarget] = useQueryParam(AUTH_QUERY_PARAM_REDIRECT, withDefault(StringParam, null));
  const navigate = useTypedNavigate();
  const rawNavigate = useNavigate();

  const getMe = useStore.useGetMe();
  const authStatus = useStore.useAuthStatus();

  // "as" is fine here because any string may be useful for analytics
  const origin = (forwardedSource as EGTAGRegistrationOrigin | undefined) || EGTAGRegistrationOrigin.DIRECT;
  // "as" is fine here because any string may be useful for analytics
  const popup = forwardedPopup as EGTAGRegistrationPopUp | undefined;

  useMount(async () => {
    try {
      if (!token) {
        navigate({ to: Routes.auth.sign_in });
        return;
      }
      await localforage.setItem(AUTH_TOKEN_KEY, token);

      // Strip just the token from the visible URL/history now that it's safely stored -
      // only after the write above succeeds, so a browser back/refresh never lands on a
      // page still carrying the token but without it saved anywhere. Done with the raw
      // History API, not use-query-params' setter, and only removes this one param
      // in place - it does not rebuild or reorder the rest of the query string.
      const url = new URL(window.location.href);
      url.searchParams.delete(AUTH_QUERY_PARAM_TOKEN);
      window.history.replaceState(null, '', url);

      const user = await getMe();
      if (requestType === 'signup' && user) {
        gtag.signUp({
          authentication_method: 'google',
          user_email: user.email,
          user_id: user.id,
          origin,
          popup,
        });
      }
      setRequestType(undefined);
      // Re-validate here too, same as SignInPage's own success handler - a query param is
      // attacker-controlled input regardless of which page put it there originally.
      if (isSafeRedirectPath(redirectTarget)) {
        rawNavigate(redirectTarget);
      } else {
        navigate({ to: Routes.index });
      }
    } catch (error) {
      // Without this, a failure anywhere above (token storage, getMe(), gtag) silently
      // bounced back to sign-in with zero trace - confirmed live on dev (2026-08-18): a
      // real, backend-issued token produced zero network calls and no console output at
      // all, because this catch swallowed whatever threw before it ever reached getMe().
      console.error('OAuth2AuthorizationPage: failed to complete Google sign-in', error);
      navigate({ to: Routes.auth.sign_in });
    }
  });

  if (authStatus === AuthStatus.UNKNOWN) {
    return null;
  }

  return <ContentLoader loading entity="Google" isEmpty={false} />;
};
